// src/utils/dates.js
import useAppConfigStore from '../store/useAppConfigStore';

export const formatDate = (dateInput) => {
  if (!dateInput) return "";

  try {
    let date;
    const isDateObj = dateInput instanceof Date;
    if (isDateObj) {
      date = dateInput;
    } else {
      const datePattern = /^\d{4}-\d{2}-\d{2}|^\d{2}-\d{2}-\d{4}|^\d{2}\/\d{2}\/\d{4}/;
      if (typeof dateInput === "string" && !datePattern.test(dateInput)) return dateInput;
      date = new Date(dateInput);
    }
    if (isNaN(date.getTime())) return dateInput;

    const { dateFormat, dateSeparator, timezoneIana } = useAppConfigStore.getState();

    let day, month, year;
    // If input is a datetime with time or 'T', or a Date object, and timezoneIana is provided, format in that timezone
    if ((isDateObj || (typeof dateInput === "string" && (dateInput.includes("T") || dateInput.includes(":")))) && timezoneIana) {
      try {
        const formatter = new Intl.DateTimeFormat("en-US", {
          timeZone: timezoneIana,
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
        });
        const parts = formatter.formatToParts(date).reduce((acc, p) => ({ ...acc, [p.type]: p.value }), {});
        day = parts.day;
        month = parts.month;
        year = parts.year;
      } catch {
        day = String(date.getDate()).padStart(2, "0");
        month = String(date.getMonth() + 1).padStart(2, "0");
        year = date.getFullYear();
      }
    } else if (typeof dateInput === "string" && /^\d{4}-\d{2}-\d{2}$/.test(dateInput.trim())) {
      // Plain date without time component: avoid browser timezone shifts
      const [y, m, d] = dateInput.trim().split("-");
      day = d;
      month = m;
      year = y;
    } else {
      day = String(date.getDate()).padStart(2, "0");
      month = String(date.getMonth() + 1).padStart(2, "0");
      year = date.getFullYear();
    }

    let formatted = "";
    switch (dateFormat) {
      case "DD MM YYYY":
        formatted = [day, month, year].join(dateSeparator);
        break;
      case "YYYY MM DD":
        formatted = [year, month, day].join(dateSeparator);
        break;
      case "MM DD YYYY":
        formatted = [month, day, year].join(dateSeparator);
        break;
      default:
        formatted = [day, month, year].join(dateSeparator);
    }

    return formatted;
  } catch {
    return dateInput;
  }
};

export const formatTime = (dateTimeString) => {
  if (!dateTimeString) return "";
  try {
    const date = new Date(dateTimeString);
    if (isNaN(date)) return dateTimeString;

    const { timezoneIana } = useAppConfigStore.getState();
    const options = {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    };
    if (timezoneIana) {
      options.timeZone = timezoneIana;
    }
    return date.toLocaleTimeString("en-US", options);
  } catch {
    return dateTimeString;
  }
};

export const formatDateTime = (dateTimeString) => {
  if (!dateTimeString) return "";

  try {
    const date = new Date(dateTimeString);
    if (isNaN(date)) return dateTimeString;

    const { dateFormat, dateSeparator, timezoneIana } = useAppConfigStore.getState();

    let day, month, year, hours, minutes, ampm;

    if (timezoneIana) {
      try {
        const formatter = new Intl.DateTimeFormat("en-US", {
          timeZone: timezoneIana,
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
          hour: "numeric",
          minute: "2-digit",
          hour12: true,
        });
        const parts = formatter.formatToParts(date).reduce((acc, p) => ({ ...acc, [p.type]: p.value }), {});
        day = parts.day;
        month = parts.month;
        year = parts.year;
        hours = parts.hour;
        minutes = parts.minute;
        ampm = parts.dayPeriod || (Number(parts.hour) >= 12 ? "PM" : "AM");
      } catch (e) {
        day = String(date.getDate()).padStart(2, "0");
        month = String(date.getMonth() + 1).padStart(2, "0");
        year = date.getFullYear();
        hours = date.getHours();
        minutes = String(date.getMinutes()).padStart(2, "0");
        ampm = hours >= 12 ? "PM" : "AM";
        hours = hours % 12 || 12;
      }
    } else {
      day = String(date.getDate()).padStart(2, "0");
      month = String(date.getMonth() + 1).padStart(2, "0");
      year = date.getFullYear();
      hours = date.getHours();
      minutes = String(date.getMinutes()).padStart(2, "0");
      ampm = hours >= 12 ? "PM" : "AM";
      hours = hours % 12 || 12;
    }

    let formattedDate = "";
    switch (dateFormat) {
      case "DD MM YYYY":
        formattedDate = [day, month, year].join(dateSeparator);
        break;
      case "YYYY MM DD":
        formattedDate = [year, month, day].join(dateSeparator);
        break;
      case "MM DD YYYY":
        formattedDate = [month, day, year].join(dateSeparator);
        break;
      default:
        formattedDate = [day, month, year].join(dateSeparator);
    }

    return `${formattedDate} ${hours}:${minutes} ${ampm}`;
  } catch {
    return dateTimeString;
  }
};

export const getTodayOrgDate = () => {
  const { timezoneIana } = useAppConfigStore.getState();
  if (timezoneIana) {
    try {
      const formatter = new Intl.DateTimeFormat("en-CA", {
        timeZone: timezoneIana,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      });
      return formatter.format(new Date());
    } catch {
      // fallback to client date
    }
  }
  const now = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
};

export const getOrgNowISO = () => {
  const { timezoneIana } = useAppConfigStore.getState();
  if (timezoneIana) {
    try {
      const formatter = new Intl.DateTimeFormat("en-US", {
        timeZone: timezoneIana,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      });
      const parts = formatter.formatToParts(new Date()).reduce((acc, p) => ({ ...acc, [p.type]: p.value }), {});
      const hour = parts.hour === "24" ? "00" : parts.hour;
      return `${parts.year}-${parts.month}-${parts.day}T${hour}:${parts.minute}`;
    } catch {
      // fallback
    }
  }
  const now = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}T${pad(now.getHours())}:${pad(now.getMinutes())}`;
};

export const getThisWeekRange = () => {
  const todayStr = getTodayOrgDate();
  const [y, m, d] = todayStr.split("-").map(Number);
  const todayDate = new Date(y, m - 1, d);
  const day = todayDate.getDay();
  const sunday = new Date(todayDate);
  sunday.setDate(todayDate.getDate() - day);
  const pad = (n) => String(n).padStart(2, "0");
  const format = (dt) => `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}`;
  return {
    from: format(sunday),
    to: todayStr,
  };
};

export const getFinancialYearRange = (financialYear, refDate = new Date()) => {
  const fy = financialYear || {};
  const startMonth = Number(fy.start_month) || 1;
  const startDay = Number(fy.start_day) || 1;
  const endMonth = Number(fy.end_month) || 12;
  const endDay = Number(fy.end_day) || 31;

  const currentYear = refDate.getFullYear();
  const currentMonth = refDate.getMonth() + 1;

  let startYear, endYear;
  if (endMonth < startMonth) {
    if (currentMonth >= startMonth) {
      startYear = currentYear;
      endYear = currentYear + 1;
    } else {
      startYear = currentYear - 1;
      endYear = currentYear;
    }
  } else {
    startYear = currentYear;
    endYear = currentYear;
  }

  const pad = (n) => String(n).padStart(2, "0");
  const actualStartDay = Math.min(startDay, new Date(startYear, startMonth, 0).getDate());
  const actualEndDay = Math.min(endDay, new Date(endYear, endMonth, 0).getDate());

  const from = `${startYear}-${pad(startMonth)}-${pad(actualStartDay)}`;
  const to = `${endYear}-${pad(endMonth)}-${pad(actualEndDay)}`;

  return { from, to };
};

export const formatDisplayDate = (dateStr) => {
  if (!dateStr) return "";
  return formatDate(dateStr);
};

export const getOverdueInfo = (item) => {
  if (!item) return { isOverdue: false, days: 0 };

  const rawStatus = (item.status || "").toLowerCase().replace(/[\s-]+/g, "_");
  if (!["open", "partially_paid"].includes(rawStatus)) {
    return { isOverdue: false, days: 0 };
  }

  if (item.is_overdue === true) {
    return {
      isOverdue: true,
      days: item.overdue_days || 0,
    };
  }

  if (!item.due_date) return { isOverdue: false, days: 0 };

  try {
    const dueDateStr = String(item.due_date).split("T")[0];
    const parts = dueDateStr.split("-").map(Number);
    if (parts.length === 3 && !parts.some(isNaN)) {
      const [year, month, day] = parts;
      const dueDate = new Date(year, month - 1, day);
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const diffMs = today.getTime() - dueDate.getTime();
      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
      if (diffDays > 0) {
        return { isOverdue: true, days: diffDays };
      }
    }
  } catch (err) {
    console.error("Error calculating overdue info:", err);
  }

  return { isOverdue: false, days: 0 };
};

export const getDueAmountTooltip = (item, t) => {
  if (!item) return "";
  const rawStatus = (item.status || "").toLowerCase().replace(/[\s-]+/g, "_");
  if (rawStatus === "draft" || rawStatus === "pending") {
    const dueDateFormatted = item.due_date ? formatDate(item.due_date) : null;
    return dueDateFormatted
      ? `${t ? t("Due Date") : "Due Date"}: ${dueDateFormatted}`
      : (t ? t("Draft") : "Draft");
  }

  const balance = Number(item.balance_amount ?? 0);
  if (rawStatus === "paid" || balance <= 0) {
    return t ? t("Fully Paid") : "Fully Paid";
  }

  const { isOverdue, days } = getOverdueInfo(item);
  const dueDateFormatted = item.due_date ? formatDate(item.due_date) : null;

  if (isOverdue) {
    const dayText = days === 1 ? (t ? t("day") : "day") : (t ? t("days") : "days");
    const afterDueText = t ? t("after due") : "after due";
    const dueDateLabel = t ? t("Due Date") : "Due Date";
    return dueDateFormatted
      ? `${days} ${dayText} ${afterDueText} (${dueDateLabel}: ${dueDateFormatted})`
      : `${days} ${dayText} ${afterDueText}`;
  }

  if (dueDateFormatted) {
    const dueDateLabel = t ? t("Due Date") : "Due Date";
    return `${dueDateLabel}: ${dueDateFormatted}`;
  }

  return t ? t("Total amount due to be paid") : "Total amount due to be paid";
};
